import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  FETAL_MOVEMENT_LABELS,
  FETAL_MOVEMENTS,
  formatGestation,
  type FetalMovement,
} from '@wodi/domain';
import type { AncContactView, AncView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  Kbd,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import { ancContactInError, ancQuery, maternityKeys, recordAncContact } from '../api/maternity.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { toasts } from '../toasts.ts';
import {
  DangerSignChecklist,
  DangerSignWords,
  embeddedPayload,
  EmbeddedVitalsFields,
  errorText,
  InErrorDialog,
  MATERNAL_VITALS,
  noteServerAlerts,
  readWhole,
  saveErrorText,
  setValues,
  useEmbeddedVitals,
  useVitalAlerts,
  useWhen,
  WhenFields,
} from './common.tsx';
import styles from './maternity.module.css';

// An ANC contact on a visit (docs/plans/phase2-maternity-mch.md §1.1 item 2, §6): structured
// fields as entered, vitals through the triage rules, danger signs from the facility's list.
// Tests and supplements are ordered and prescribed in the consultation on this visit.

const route = getRouteApi('/_app/visits/$visitId/anc');
const NOT_SAID = 'not_said';

function AncForm({ view }: { view: AncView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const vitals = useEmbeddedVitals(view.patientAgeDays);
  const alerts = useVitalAlerts(vitals, MATERNAL_VITALS);
  const when = useWhen();
  const active = view.contacts.filter((c) => c.status === 'active');
  const [contactNumber, setContactNumber] = useState(String(active.length + 1));
  const [fundal, setFundal] = useState('');
  const [fhr, setFhr] = useState('');
  const [presentation, setPresentation] = useState('');
  const [movement, setMovement] = useState<string>(NOT_SAID);
  const [codes, setCodes] = useState<string[]>([]);
  const [none, setNone] = useState(false);
  const [nextVisit, setNextVisit] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const requestId = useRef(crypto.randomUUID());

  const numbers = {
    contactNumber: readWhole(contactNumber),
    fundal: readWhole(fundal),
    fhr: readWhole(fhr),
  };
  const payload = embeddedPayload(vitals, alerts);
  const invalid =
    numbers.contactNumber === null ||
    Object.values(numbers).some((n) => n === 'invalid') ||
    when.invalid ||
    !payload.ready;
  const n = (v: number | null | 'invalid') => (v === 'invalid' ? null : v);

  const save = useMutation({
    mutationFn: () =>
      recordAncContact(view.visit.id, {
        clientRequestId: requestId.current,
        contactNumber: n(numbers.contactNumber) ?? 1,
        ...(when.value === undefined ? {} : { contactAt: when.value }),
        fundalHeightCm: n(numbers.fundal),
        fetalHeartRate: n(numbers.fhr),
        presentation: presentation.trim() === '' ? null : presentation.trim(),
        fetalMovement: movement === NOT_SAID ? null : (movement as FetalMovement),
        dangerSignCodes: codes,
        noDangerSigns: none,
        nextVisitDate: nextVisit,
        note: note.trim() === '' ? null : note.trim(),
        vitals: payload.vitals,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.anc(view.visit.id), next);
      void queryClient.invalidateQueries({ queryKey: maternityKeys.patient(view.visit.patientId) });
      requestId.current = crypto.randomUUID();
      setFundal('');
      setFhr('');
      setPresentation('');
      setMovement(NOT_SAID);
      setCodes([]);
      setNone(false);
      setNextVisit(null);
      setNote('');
      vitals.setTexts({});
      vitals.setReasons({});
      vitals.setServerAlerts([]);
      setTried(false);
      setContactNumber(String(next.contacts.filter((c) => c.status === 'active').length + 1));
      toasts.show({ title: 'ANC contact saved' });
    },
    onError: (error) => {
      noteServerAlerts(error, vitals);
      requestId.current = crypto.randomUUID();
    },
  });
  const submit = () => {
    setTried(true);
    if (!online || invalid || save.isPending) return;
    save.mutate();
  };
  useShortcut('mod+enter', submit, { allowInFields: true, description: 'Save ANC contact' });
  const whole = (v: number | null | 'invalid') =>
    v === 'invalid' ? { errorMessage: 'A whole number.' } : {};
  const saveError = saveErrorText(save.error, 'The contact was not saved. Try again.');

  return (
    <form
      className={styles.form}
      aria-label="Record ANC contact"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className={styles.when}>
        <TextField
          label="Contact number"
          width="xs"
          inputMode="numeric"
          value={contactNumber}
          onChange={setContactNumber}
          {...whole(numbers.contactNumber)}
        />
        <WhenFields label="Seen" when={when} />
      </div>
      <EmbeddedVitalsFields state={vitals} keys={MATERNAL_VITALS} tried={tried} />
      <div className={styles.grid}>
        <TextField
          label="Fundal height"
          unit="cm"
          inputMode="numeric"
          value={fundal}
          onChange={setFundal}
          {...whole(numbers.fundal)}
        />
        <TextField
          label="Fetal heart rate"
          unit="/min"
          inputMode="numeric"
          value={fhr}
          onChange={setFhr}
          {...whole(numbers.fhr)}
        />
        <TextField label="Presentation" value={presentation} onChange={setPresentation} />
      </div>
      <RadioGroup
        label="Fetal movements"
        orientation="horizontal"
        value={movement}
        onChange={setMovement}
        options={[
          { id: NOT_SAID, label: 'Not recorded' },
          ...FETAL_MOVEMENTS.map((m) => ({ id: m, label: FETAL_MOVEMENT_LABELS[m] })),
        ]}
      />
      <DangerSignChecklist
        label="Danger signs"
        list={view.dangerSigns}
        codes={codes}
        onCodes={setCodes}
        none={none}
        onNone={setNone}
      />
      <DateField label="Next visit" value={nextVisit} onChange={setNextVisit} />
      <TextArea label="Note" value={note} onChange={setNote} rows={3} />
      {saveError === null ? null : <Notice tone="error">{saveError}</Notice>}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save ANC contact
        </Button>
        <Kbd combo="mod+enter" />
      </div>
    </form>
  );
}

function Contacts({ view, mayRecord }: { view: AncView; mayRecord: boolean }) {
  const queryClient = useQueryClient();
  const [marking, setMarking] = useState<AncContactView | null>(null);
  const mark = useMutation({
    mutationFn: (reason: string) => ancContactInError(marking?.id ?? '', reason),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.anc(view.visit.id), next);
      toasts.show({ title: 'ANC contact entered in error' });
      setMarking(null);
    },
  });
  return (
    <>
      <Table flat dense aria-label="ANC contacts in this pregnancy">
        <TableHeader>
          <Column isRowHeader>Contact</Column>
          <Column>Gestation</Column>
          <Column>Vitals</Column>
          <Column>Findings</Column>
          <Column>Danger signs</Column>
          <Column>State</Column>
        </TableHeader>
        <TableBody items={view.contacts} renderEmptyState={() => 'No ANC contacts yet.'}>
          {(c) => (
            <Row id={c.id}>
              <Cell>
                {`Contact ${String(c.contactNumber)}, `}
                <span className={styles.numeric}>{formatInstant(c.contactAt)}</span>
              </Cell>
              <Cell>{c.gestation === null ? 'Not known' : formatGestation(c.gestation)}</Cell>
              <Cell>{setValues(c.vitalSet)}</Cell>
              <Cell>
                {[
                  c.fundalHeightCm === null ? null : `Fundal height ${String(c.fundalHeightCm)} cm`,
                  c.fetalHeartRate === null ? null : `FHR ${String(c.fetalHeartRate)}/min`,
                  c.presentation,
                  c.fetalMovement === null
                    ? null
                    : `Fetal movements: ${FETAL_MOVEMENT_LABELS[c.fetalMovement].toLowerCase()}`,
                  c.note,
                ]
                  .filter((x) => x !== null)
                  .join('; ') || 'None entered'}
              </Cell>
              <Cell>
                <DangerSignWords signs={c.dangerSigns} none={c.noDangerSigns} />
              </Cell>
              <Cell>
                {c.status === 'entered_in_error' ? (
                  <Tag state="neutral">Entered in error</Tag>
                ) : mayRecord ? (
                  <Button
                    variant="quiet"
                    dense
                    onPress={() => {
                      setMarking(c);
                    }}
                  >
                    Enter in error
                  </Button>
                ) : (
                  <Tag state="done">Recorded</Tag>
                )}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {marking === null ? null : (
        <InErrorDialog
          what="this ANC contact"
          onClose={() => {
            setMarking(null);
          }}
          onConfirm={(reason) => {
            mark.mutate(reason);
          }}
          pending={mark.isPending}
          error={mark.error}
        />
      )}
    </>
  );
}

export function AncPage() {
  const { visitId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const anc = useQuery(ancQuery(visitId));
  if (session === null || !can(session, 'maternity.read')) {
    return (
      <Notice tone="info">
        You don't have permission to see maternity records. Ask an administrator if you need it.
      </Notice>
    );
  }
  if (anc.isPending) return <LoadingBlock label="Loading ANC" rows={8} />;
  if (anc.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void anc.refetch()}>Try again</Button>}>
        {errorText(anc.error, 'ANC could not be loaded. Try again.')}
      </Notice>
    );
  }
  const view = anc.data;
  const mayRecord = can(session, 'maternity.record');
  const open = view.visit.status === 'open';
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={view.visit.patientId} />
      <PageHeader
        title="ANC contact"
        {...(view.pregnancy?.gestationToday == null
          ? {}
          : { description: `Gestation today ${formatGestation(view.pregnancy.gestationToday)}.` })}
        actions={
          <nav className={styles.links} aria-label="Related">
            <Link to="/patients/$patientId/maternity" params={{ patientId: view.visit.patientId }}>
              Maternity record
            </Link>
          </nav>
        }
      />
      {online ? null : (
        <Notice tone="info">No connection to Wodi. Saving waits for the connection.</Notice>
      )}
      {view.pregnancy === null ? (
        <Notice tone="info">
          This patient has no open pregnancy. Open it on the maternity record, then record the
          contact here.
        </Notice>
      ) : null}
      {!open ? (
        <Notice tone="info">
          This visit is closed. Its contacts are shown; nothing new can be added.
        </Notice>
      ) : null}
      {mayRecord && open && view.pregnancy !== null ? (
        <Section title="Record ANC contact">
          <AncForm view={view} />
        </Section>
      ) : null}
      <Section
        title="Tests and supplements on this visit"
        description="Ordered and prescribed in the consultation, with all its checks."
        actions={
          can(session, 'consultation.record') && open ? (
            <Link to="/visits/$visitId/consultation" params={{ visitId: view.visit.id }}>
              Order tests or prescribe
            </Link>
          ) : undefined
        }
      >
        {view.orders.length === 0 ? (
          <p className={styles.meta}>Nothing ordered or prescribed on this visit yet.</p>
        ) : (
          <ul className={styles.form} aria-label="Orders on this visit">
            {view.orders.map((o) => (
              <li key={o.id}>{`${o.name} (${o.kind}), ${o.status.replaceAll('_', ' ')}`}</li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="ANC contacts in this pregnancy">
        <Contacts view={view} mayRecord={mayRecord && open} />
      </Section>
    </div>
  );
}
